import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { builderSetShouldEditSetup } from '~/actions/builder';
import DocumentViewer from '~/components/Shared/DocumentViewer_DEPRECATED/DocumentViewer';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { LoadingContent } from '~/eds/src/components/LoadingContent/LoadingContent';
import { WorkflowIntakeFormType } from '~/enums/WorkflowIntakeFormType';
import { api } from '~/redux/api/createApi';
import Alert from '~/ui/components/Alert/Alert';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import Text from '~/ui/components/Text/Text';

const { Company, CompanyAndCounterparty, Counterparty } =
  WorkflowIntakeFormType;

const LazyDocumentEditor = React.lazy(() =>
  import('~/components/Shared/DocumentEditor/DocumentEditor').then(
    (module) => ({
      default: module.DocumentEditor,
    }),
  ),
);

function Source({
  // connected
  builderSetShouldEditSetup,
  disabled,
  workflow,
}) {
  const { id, type, file: uploadedFile } = workflow;
  const canRenderFile = type !== Counterparty && !uploadedFile; // we cannot render a file if it doesn't exist (counterparty) or if users have only uploaded it locally
  const {
    data: templateFile,
    isError,
    isFetching: isLoadingFile,
  } = api.endpoints.getWorkflowFile.useQuery(
    { id, type: 'sfdt' },
    { skip: !id || !canRenderFile },
  );
  const { toast } = useToast();
  const intl = useIntl();

  useEffect(() => {
    if (isError) {
      toast({
        message: intl.formatMessage({
          id: 'Source.fetchErrorMessage',
          defaultMessage:
            'An error occurred while fetching file. Please try again.',
          description: 'Error message shown when file fetching fails',
        }),
        status: 'danger',
      });
    }
  }, [isError, toast]);

  let permissionsText;
  if (type === CompanyAndCounterparty) {
    permissionsText = intl.formatMessage({
      id: 'Source.companyAndCounterpartyPermissions',
      defaultMessage:
        'Users can upload an existing document and generate contracts from a template.',
      description: 'Permission text for company and counterparty workflow type',
    });
  } else if (type === Company) {
    permissionsText = intl.formatMessage({
      id: 'Source.companyPermissions',
      defaultMessage: 'Users can generate contracts from a template.',
      description: 'Permission text for company workflow type',
    });
  } else if (type === Counterparty) {
    permissionsText = intl.formatMessage({
      id: 'Source.counterpartyPermissions',
      defaultMessage: 'Users can upload an existing document.',
      description: 'Permission text for counterparty workflow type',
    });
  }

  function renderDocViewer() {
    if (templateFile) {
      return (
        <React.Suspense fallback={<LoadingContent />}>
          <LazyDocumentEditor file={templateFile} readonly />
        </React.Suspense>
      );
    } else {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <DocumentViewer
            file={templateFile}
            maxWidth="100%"
            noFileMessage={intl.formatMessage({
              id: 'Source.noFileMessage',
              defaultMessage: 'Unable to find file.',
              description: 'Message shown when no file is found',
            })}
          />
        </Box>
      );
    }
  }

  return (
    <FlexLayout
      disabled={disabled}
      flexDirection="column"
      space={6}
      sx={{ height: '100%' }}
    >
      <FlexLayout space={2} flexDirection="column">
        <Alert
          variant="info"
          noActionBorder
          actions={[
            {
              text: intl.formatMessage({
                id: 'Source.editAction',
                defaultMessage: 'Edit',
                description: 'Edit button text in alert',
              }),
              onClick: () => builderSetShouldEditSetup(true),
            },
          ]}
        >
          <Text color="gray-700" variant="xs-dense-medium">
            {uploadedFile
              ? intl.formatMessage({
                  id: 'Source.uploadedFileMessage',
                  defaultMessage:
                    'You have uploaded a new template for this workflow. To preview the workflow with the new template, please save the workflow.',
                  description:
                    'Message shown when user has uploaded a new template',
                })
              : permissionsText}
          </Text>
        </Alert>
      </FlexLayout>
      {isLoadingFile ? (
        <FlexLayout alignItems="center" justifyContent="center">
          <LoadingSpinner />
        </FlexLayout>
      ) : !canRenderFile ? (
        <Box sx={{ flexGrow: 1 }}>
          <Icon icon="uploadPaper" sx={{ height: '100%', width: '100%' }} />
        </Box>
      ) : (
        renderDocViewer()
      )}
    </FlexLayout>
  );
}

const mapStateToProps = ({ builder, workflow }) => ({
  disabled: !builder.shouldEnableAllComponents,
  workflow,
});

export default connect(mapStateToProps, { builderSetShouldEditSetup })(Source);
